import { useAuth } from "@/context/AuthContext";
import Ionicons from "@expo/vector-icons/Ionicons";
import { router } from "expo-router";
import { getApp } from "firebase/app";
import {
  getFirestore as getWebFirestore,
  doc as webDoc,
  serverTimestamp as webServerTimestamp,
  updateDoc as webUpdateDoc,
} from "firebase/firestore";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Pressable, StyleSheet, View } from "react-native";

import { ThemedText } from "./ThemedText";

const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

const AMBER_BG = "#FFF7E6";
const AMBER_BORDER = "#FFE4A3";
const AMBER_TEXT = "#7A4A00";
const WHATSAPP_GREEN = "#25D366";
const CTA_BG = "#204169";
const CTA_TEXT = "#FFFFFF";

type DismissedAt = Date | { toDate?: () => Date } | string | null | undefined;

function toDate(value: DismissedAt): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === "object" && typeof value.toDate === "function") {
    try {
      return value.toDate();
    } catch {
      return null;
    }
  }
  if (typeof value === "string") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

function isRecent(value: DismissedAt): boolean {
  const date = toDate(value);
  if (!date) return false;
  return Date.now() - date.getTime() < SNOOZE_MS;
}

async function persistDismissal(uid: string): Promise<void> {
  const ref = webDoc(getWebFirestore(getApp()), "users", uid);
  await webUpdateDoc(ref, {
    "dismissedBanners.phoneVerify": webServerTimestamp(),
  });
}

export function PhoneVerifyBanner() {
  const { user, userData } = useAuth();
  const { t } = useTranslation();
  const [locallyDismissed, setLocallyDismissed] = useState(false);

  if (!user || !userData) return null;
  // Hide only when the Firebase Auth account actually has a phone provider
  // linked AND the app's own verified flag is set. Either one alone can
  // drift — the Firestore field can be stale after an unlink, and a
  // race can briefly leave the flag unset right after linking.
  const hasPhoneProvider =
    user.providerData?.some((p) => p?.providerId === "phone") ?? false;
  const isFirestoreVerified = userData.phoneNumber?.verified === true;
  if (hasPhoneProvider && isFirestoreVerified) return null;
  if (locallyDismissed) return null;
  if (isRecent(userData.dismissedBanners?.phoneVerify)) return null;

  const handleDismiss = () => {
    setLocallyDismissed(true);
    persistDismissal(user.uid).catch((err) => {
      console.warn("Failed to persist phone-verify banner dismissal", err);
    });
  };

  const handleVerify = () => {
    router.push("/settings-signin-methods" as never);
  };

  return (
    <View style={styles.container}>
      <Ionicons
        name="logo-whatsapp"
        size={22}
        color={WHATSAPP_GREEN}
        style={styles.icon}
      />
      <ThemedText
        style={styles.message}
        lightColor={AMBER_TEXT}
        darkColor={AMBER_TEXT}
      >
        {t("PHONE_VERIFY_BANNER_MESSAGE")}
      </ThemedText>
      <Pressable
        onPress={handleVerify}
        style={({ pressed }) => [styles.cta, pressed && styles.ctaPressed]}
        accessibilityRole="button"
      >
        <ThemedText
          style={styles.ctaText}
          lightColor={CTA_TEXT}
          darkColor={CTA_TEXT}
        >
          {t("PHONE_VERIFY_BANNER_CTA")}
        </ThemedText>
      </Pressable>
      <Pressable
        onPress={handleDismiss}
        style={({ pressed }) => [styles.close, pressed && styles.closePressed]}
        accessibilityRole="button"
        accessibilityLabel={t("PHONE_VERIFY_BANNER_CLOSE")}
        hitSlop={8}
      >
        <Ionicons name="close" size={18} color={AMBER_TEXT} />
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: AMBER_BG,
    borderBottomWidth: 1,
    borderBottomColor: AMBER_BORDER,
    paddingHorizontal: 12,
    paddingVertical: 10,
    gap: 8,
  },
  icon: {
    marginRight: 2,
  },
  message: {
    flex: 1,
    fontSize: 13,
    lineHeight: 18,
  },
  cta: {
    backgroundColor: CTA_BG,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  ctaPressed: {
    opacity: 0.8,
  },
  ctaText: {
    fontSize: 13,
    fontWeight: "600",
  },
  close: {
    padding: 4,
  },
  closePressed: {
    opacity: 0.5,
  },
});
